import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import { Add, DeleteOutline, InfoOutlined } from "@mui/icons-material";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { setNewConditionLoan } from "@store/slices/registerSlice";
import {
  useLazyGetConditionsByIdQuery,
  usePostValidateNextStageMutation,
  usePutConditionMutation,
} from "@api/applications";
import { ApiError } from "@interfaces/response";
import { FactoringDebtorPatchPayload } from "@interfaces/factoring";
import {
  useDeleteFactoringDebtorMutation,
  useLazyGetFactoringDebtorsQuery,
  usePatchFactoringDebtorMutation,
  usePostFactoringDebtorMutation,
} from "@api/factoring";
import useStatusConfig from "@hooks/useStatusConfig";
import { Button, Callout, DialogConfirm, PageHeader, StepActions } from "@components/ui_v2";
import {
  BarRepartoLinea,
  CalloutEmpresasFaltantes,
  CardEmpresaLinea,
  BoxComparacionLinea,
  EmpresaLinea,
  PaperLineaSolicitada,
  getRfcEmpresaError,
} from "@components/factoraje";
import { formatCurrencyWhole } from "@helpers/funciones";
import { PATHS_TOFU_V2, STATUS_CATALOG } from "@helpers/constantes";

/**
 * "LÍNEA · CLIENTES" (`FX_ClientesUso` del diseño de Flux): el usuario declara con qué empresas
 * (deudores) va a factorar y cuánto piensa ocupar con cada una. La línea solicitada sube sola si
 * los montos suman más de lo que pidió en el cotizador y regresa a lo pedido si bajan.
 *
 * El monto solicitado y el máximo de la línea salen de la condición activa de la solicitud (mismo
 * servicio que `OfertaDefault`): `selected_amount` es lo pedido en el cotizador y `approved_amount`
 * el máximo, que al registrar la solicitud se llena con el `max_amount` del régimen.
 *
 * Las empresas se guardan como deudores de factoraje (`/solicitudes/{id}/deudores_factoraje`): al
 * cargar se prellenan las activas y al continuar se sincroniza la lista (alta, cambios y bajas).
 */

/** Respaldo si no se puede leer la condición: el store y, si tampoco, el default del diseño. */
const DEFAULT_LINEA = 3_000_000;
/** Respaldo del máximo de la línea si la condición no trae `approved_amount`. */
const DEFAULT_MAX_LINEA = 10_000_000;

/** Esqueleto mientras cargan la condición y las empresas guardadas: tarjeta de la línea y dos filas. */
const SkeletonUsoLinea = () => (
  <Box>
    <Paper variant="lendia_paper" sx={{ p: "20px 22px 18px" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" animation="wave" width={160} height={18} />
          <Skeleton variant="text" animation="wave" width={220} height={44} />
        </Box>
        <Box>
          <Skeleton variant="text" animation="wave" width={90} height={34} />
          <Skeleton variant="text" animation="wave" width={90} height={16} />
        </Box>
      </Box>
      <Skeleton variant="rounded" animation="wave" height={12} sx={{ mt: 2, borderRadius: "6px" }} />
    </Paper>
    <Skeleton variant="text" animation="wave" width={240} height={18} sx={{ mt: "20px", mb: "10px" }} />
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <Skeleton variant="rounded" animation="wave" height={76} sx={{ borderRadius: "12px" }} />
      <Skeleton variant="rounded" animation="wave" height={76} sx={{ borderRadius: "12px" }} />
    </Box>
  </Box>
);

let empresaSeq = 0;
const nuevaEmpresa = (): EmpresaLinea => ({ id: `empresa-${(empresaSeq += 1)}`, nombre: "", rfc: "", monto: 0 });

export const UsoLinea = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { nextStatus, previousStatus } = useStatusConfig(STATUS_CATALOG.uso_de_linea.code);
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [getConditions] = useLazyGetConditionsByIdQuery();
  const [putCondition] = usePutConditionMutation();
  /** Id de la condición activa: se actualiza si la línea final cambia respecto a lo pedido. */
  const [conditionId, setConditionId] = useState("");
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);

  // Condiciones solicitadas al inicio: hasta que llegan se usa lo que haya en el store.
  const [solicitadoIni, setSolicitadoIni] = useState(application.loan_amount || DEFAULT_LINEA);
  const [maxLinea, setMaxLinea] = useState(DEFAULT_MAX_LINEA);

  // Línea que el usuario pidió; sube sola si los montos suman más y regresa a la pedida si bajan.
  const [lineaBase, setLineaBase] = useState(solicitadoIni);

  const [empresas, setEmpresas] = useState<EmpresaLinea[]>(() => [nuevaEmpresa()]);

  // Deudores de factoraje (empresas con las que se usará la línea).
  const [getDebtors] = useLazyGetFactoringDebtorsQuery();
  const [postDebtor] = usePostFactoringDebtorMutation();
  const [patchDebtor] = usePatchFactoringDebtorMutation();
  const [deleteDebtor] = useDeleteFactoringDebtorMutation();
  /** Cómo quedó cada deudor en el servidor (por `debtorId`): base para mandar solo lo que cambió. */
  const savedDebtors = useRef(new Map<string, { nombre: string; rfc: string; monto: number }>());

  /**
   * Carga inicial con respaldo: cada consulta es independiente; si falla, se reporta a Sentry y la
   * pantalla sigue con el monto del store, el máximo por default y una empresa vacía.
   */
  const getInitialData = useCallback(async () => {
    setIsLoadingInitial(true);
    const [conditionsResult, debtorsResult] = await Promise.allSettled([
      getConditions(application.id).unwrap(),
      getDebtors({ loanApplicationId: application.id }).unwrap(),
    ]);

    if (debtorsResult.status === "fulfilled" && debtorsResult.value.length) {
      savedDebtors.current = new Map(
        debtorsResult.value.map((debtor) => [
          debtor.id,
          { nombre: debtor.company_name, rfc: debtor.rfc || "", monto: debtor.amount || 0 },
        ]),
      );
      setEmpresas(
        debtorsResult.value.map((debtor) => ({
          id: debtor.id,
          debtorId: debtor.id,
          nombre: debtor.company_name,
          rfc: debtor.rfc || "",
          monto: debtor.amount || 0,
        })),
      );
    } else if (debtorsResult.status === "rejected") {
      Sentry.captureException(debtorsResult.reason, { extra: { component: "UsoLinea-debtors" } });
    }

    try {
      if (conditionsResult.status === "rejected") throw conditionsResult.reason;
      const conditions = conditionsResult.value;
      const activeCondition = conditions.find((item) => item.active);
      if (activeCondition) {
        setConditionId(activeCondition.id);
        const solicitado = activeCondition.selected_amount || activeCondition.loan_amount;
        if (solicitado) {
          setSolicitadoIni(solicitado);
          setLineaBase(solicitado);
        }
        if (activeCondition.approved_amount) setMaxLinea(activeCondition.approved_amount);
      }
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "UsoLinea-initial_fetch" } });
    } finally {
      setIsLoadingInitial(false);
    }
  }, [application.id, getConditions, getDebtors]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);
  const [intento, setIntento] = useState(false);
  const [confirmar, setConfirmar] = useState(false);

  const total = useMemo(() => empresas.reduce((sum, empresa) => sum + (empresa.monto || 0), 0), [empresas]);
  const linea = Math.max(lineaBase, Math.min(total, maxLinea));
  const libre = Math.max(0, linea - total);
  const ajustada = total > lineaBase;
  const topeado = total >= maxLinea;
  const faltantes = empresas.filter((empresa) => !empresa.nombre.trim()).length;
  // El RFC es opcional, pero si se captura debe ser válido (`getRfcEmpresaError`).
  const rfcsInvalidos = empresas.filter((empresa) => getRfcEmpresaError(empresa.rfc)).length;

  const updateEmpresa = (id: string, patch: Partial<EmpresaLinea>) =>
    setEmpresas((prev) =>
      prev.map((empresa) => {
        if (empresa.id !== id) return empresa;
        if (patch.monto !== undefined) {
          // Entre todas las empresas no se puede pasar del máximo de la línea.
          const otros = prev.reduce((sum, x) => sum + (x.id === id ? 0 : x.monto || 0), 0);
          return { ...empresa, monto: Math.max(0, Math.min(patch.monto, maxLinea - otros)) };
        }
        return { ...empresa, ...patch };
      }),
    );

  /**
   * Sincroniza las empresas con los deudores de factoraje del servidor: baja de las que se quitaron,
   * alta de las nuevas y PATCH solo con los campos que cambiaron. Lo ya guardado se marca en el
   * estado para que, si un paso posterior falla, reintentar no duplique empresas.
   */
  /** Empresa ya registrada que el usuario pidió eliminar (pendiente de confirmar en el modal). */
  const [empresaAEliminar, setEmpresaAEliminar] = useState<EmpresaLinea | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  /** Quitar una fila: si ya está registrada se confirma y se borra en el servidor; si no, solo se quita. */
  const handleRemove = (empresa: EmpresaLinea) => {
    if (empresa.debtorId) {
      setEmpresaAEliminar(empresa);
      return;
    }
    setEmpresas((prev) => prev.filter((x) => x.id !== empresa.id));
  };

  const confirmarEliminar = async () => {
    if (!empresaAEliminar?.debtorId) return;
    setIsDeleting(true);
    try {
      await deleteDebtor({ loanApplicationId: application.id, debtorId: empresaAEliminar.debtorId }).unwrap();
      savedDebtors.current.delete(empresaAEliminar.debtorId);
      setEmpresas((prev) => prev.filter((x) => x.id !== empresaAEliminar.id));
      setEmpresaAEliminar(null);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "UsoLinea-delete_debtor" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsDeleting(false);
    }
  };

  const syncDebtors = async () => {
    const loanApplicationId = application.id;
    const currentIds = new Set(empresas.map((empresa) => empresa.debtorId).filter(Boolean));

    const removed = [...savedDebtors.current.keys()].filter((debtorId) => !currentIds.has(debtorId));
    await Promise.all(removed.map((debtorId) => deleteDebtor({ loanApplicationId, debtorId }).unwrap()));
    removed.forEach((debtorId) => savedDebtors.current.delete(debtorId));

    const created = new Map<string, string>();
    await Promise.all(
      empresas.map(async (empresa) => {
        const values = { nombre: empresa.nombre.trim(), rfc: empresa.rfc.trim(), monto: empresa.monto || 0 };

        if (!empresa.debtorId) {
          const debtor = await postDebtor({
            loanApplicationId,
            body: { company_name: values.nombre, rfc: values.rfc || null, amount: values.monto },
          }).unwrap();
          created.set(empresa.id, debtor.id);
          savedDebtors.current.set(debtor.id, values);
          return;
        }

        const saved = savedDebtors.current.get(empresa.debtorId);
        const body: FactoringDebtorPatchPayload = {};
        if (saved?.nombre !== values.nombre) body.company_name = values.nombre;
        if (saved?.rfc !== values.rfc) body.rfc = values.rfc || null;
        if (saved?.monto !== values.monto) body.amount = values.monto;
        if (!Object.keys(body).length) return;

        await patchDebtor({ loanApplicationId, debtorId: empresa.debtorId, body }).unwrap();
        savedDebtors.current.set(empresa.debtorId, values);
      }),
    );

    if (created.size) {
      setEmpresas((prev) =>
        prev.map((empresa) => (created.has(empresa.id) ? { ...empresa, debtorId: created.get(empresa.id) } : empresa)),
      );
    }
  };

  /** Avanza el status CSADL y navega a la siguiente pantalla del `status_config`. */
  const aplicar = async () => {
    setIsSubmitting(true);
    try {
      if (!nextStatus) {
        throw new Error("No se encontró el siguiente status");
      }
      await syncDebtors();

      // Si la línea final cambió respecto a lo pedido (p. ej. las empresas suman más), se actualiza la
      // condición activa: solo `selected_amount` y `loan_amount`.
      if (conditionId && linea !== solicitadoIni) {
        await putCondition({
          id: conditionId,
          body: { loan_application: application.id, selected_amount: linea, loan_amount: linea },
        }).unwrap();
        // Store: solo cambia el monto; el resto se reescribe con lo que ya tenía la solicitud.
        dispatch(
          setNewConditionLoan({
            loanAmount: linea,
            loanTerm: application.loan_term,
            amountMonthly: application.amount_monthly,
            approved_amount: application.approved_amount,
            approved_term: application.approved_term,
            interest_rate: application.interest_rate,
          }),
        );
      }

      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: nextStatus.code,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();
      navigate(nextStatus.url);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "UsoLinea-submit" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const continuar = () => {
    if (faltantes || rfcsInvalidos) {
      setIntento(true);
      return;
    }
    if (total !== solicitadoIni) setConfirmar(true);
    else aplicar();
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Línea · Clientes"
        title="¿Con qué clientes vas a usar la línea?"
        subtitle="Dinos con qué empresas planeas factorar y cuánto piensas ocupar con cada una. Al revisar tu facturación validaremos a cada deudor y su plazo de pago."
      />

      {isLoadingInitial ? (
        <SkeletonUsoLinea />
      ) : (
        <>
          <PaperLineaSolicitada
            label="Línea que quieres solicitar"
            linea={linea}
            onChangeLinea={(value) => setLineaBase(Math.min(value, maxLinea))}
            libre={libre}
            testId="uso-linea-linea-input"
          >
            {ajustada && (
              <Box sx={{ mt: "14px" }}>
                <Callout tone="warn" icon={<InfoOutlined sx={{ fontSize: 14, mt: "3px", color: "warning.main" }} />}>
                  Ajustamos tu línea solicitada a <strong>{formatCurrencyWhole(linea)}</strong> porque los montos por
                  empresa suman más. Si buscas solicitar menos, baja los montos asignados por empresa
                  {topeado ? `. El máximo de la línea es ${formatCurrencyWhole(maxLinea)}` : ""}.
                </Callout>
              </Box>
            )}
            <BarRepartoLinea segmentos={empresas} linea={linea} />
          </PaperLineaSolicitada>

          {/* `.fxu-list-h` */}
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: "0.09em",
              color: "text.secondary",
              mt: "20px",
              mb: "10px",
            }}
          >
            EMPRESAS CON LAS QUE VAS A OPERAR
          </Typography>

          {intento && faltantes > 0 && <CalloutEmpresasFaltantes faltantes={faltantes} />}

          <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {empresas.length === 0 && (
              <Box sx={{ border: "1px dashed #D5DCE4", borderRadius: "12px", p: "18px 16px", textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Aún no agregas empresas. Agrega al menos una para continuar.
                </Typography>
              </Box>
            )}
            {empresas.map((empresa, i) => (
              <CardEmpresaLinea
                key={empresa.id}
                empresa={empresa}
                index={i}
                onChange={(patch) => updateEmpresa(empresa.id, patch)}
                onRemove={() => handleRemove(empresa)}
                nombreFaltante={intento && !empresa.nombre.trim()}
                // Como el RFC del registro: el error sale al intentar continuar o en cuanto ya tiene 12 posiciones.
                rfcError={intento || empresa.rfc.trim().length >= 12 ? getRfcEmpresaError(empresa.rfc) : ""}
              />
            ))}
          </Box>

          {/* `.fxu-foot` — agregar empresa y total solicitado */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              mt: "14px",
            }}
          >
            <Button
              tone="outline"
              startIcon={<Add fontSize="small" />}
              onClick={() => setEmpresas((prev) => [...prev, nuevaEmpresa()])}
              disabled={isSubmitting}
              data-testid="uso-linea-agregar-button"
            >
              Agregar empresa
            </Button>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Hasta ahora llevas solicitado
              </Typography>
              <Typography
                sx={{ fontSize: 16, fontWeight: 700, color: "text.primary", fontVariantNumeric: "tabular-nums" }}
              >
                {formatCurrencyWhole(total)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mt: "14px" }}>
            {/* `.callout` informativo: fondo y borde de la marca (`tokens.infoBg` / `infoBorder`), ícono en su color. */}
            <Callout tone="info" icon={<InfoOutlined sx={{ fontSize: 14, mt: "3px" }} />}>
              Con tu facturación confirmaremos el plazo de pago y el límite que podemos autorizar por cada empresa.
              Podrás redistribuir tu línea cuando la recibas.
            </Callout>
          </Box>
        </>
      )}

      <StepActions
        // Regreso secuencial según `status_config`; la ruta fija queda de respaldo.
        onBack={() => navigate(previousStatus?.url || PATHS_TOFU_V2.consultaCiec.path)}
        onNext={continuar}
        nextDisabled={empresas.length === 0 || total === 0 || isSubmitting || isLoadingInitial}
        backTestId="uso-linea-back-button"
        nextTestId="uso-linea-continue-button"
      />

      <DialogConfirm
        open={Boolean(empresaAEliminar)}
        tone="danger"
        icon={<DeleteOutline />}
        title="¿Eliminar esta empresa?"
        message={
          <>
            <strong>{empresaAEliminar?.nombre.trim() || "Esta empresa"}</strong> ya está registrada en tu solicitud.
            ¿Seguro que deseas eliminarla?
          </>
        }
        cancelLabel="Cancelar"
        confirmLabel="Sí, eliminar"
        loadingLabel="Eliminando…"
        loading={isDeleting}
        onClose={() => setEmpresaAEliminar(null)}
        onConfirm={confirmarEliminar}
        cancelTestId="uso-linea-eliminar-cancelar-button"
        confirmTestId="uso-linea-eliminar-confirmar-button"
      />

      <DialogConfirm
        open={confirmar}
        layout="sheet"
        title="Confirma el monto de tu línea"
        message={
          <>
            En tu cotización pediste <strong>{formatCurrencyWhole(solicitadoIni)}</strong>. Con las empresas que
            registraste estás solicitando <strong>{formatCurrencyWhole(total)}</strong>
            {total > solicitadoIni ? ", más de lo que habías pedido." : ", menos de lo que habías pedido."}
          </>
        }
        cancelLabel="Volver a editar"
        confirmLabel="Confirmar y continuar"
        loadingLabel="Guardando…"
        loading={isSubmitting}
        onClose={() => setConfirmar(false)}
        onConfirm={aplicar}
        cancelTestId="uso-linea-editar-button"
        confirmTestId="uso-linea-confirmar-button"
      >
        <BoxComparacionLinea solicitado={solicitadoIni} nuevo={total} />
      </DialogConfirm>
    </Box>
  );
};
